import Paging from 'components/paging';
import TableSummary from './TableSummary';
import {
  Card as DesignCard,
  CardHeading as DesignCardHeading,
  DataTable as DesignDataTable,
  FilterPanel as DesignFilterPanel,
  ListToolbar as DesignListToolbar,
} from 'components/pulsalink-ui';

export { default as Input } from 'components/input/Input';
export { default as NativeSelect } from 'components/input/NativeSelect';
export { default as Textarea } from 'components/input/Textarea';
export { default as Select } from 'components/select-tailwinds';
export { default as DateRangePicker } from 'components/date-picker/DateRangePicker';
export { default as Pagination } from 'components/paging';
export { default as TableSummary } from './TableSummary';
export { ListMeta } from 'components/pulsalink-ui';

export function ListCard({ children, className = '', compact = false, ...props }) {
  const spacingClass = compact
    ? 'space-y-0! [&>*:first-child]:pb-2! [&_[data-list-filter]]:py-3!'
    : 'space-y-3';

  return (
    <DesignCard as='div' {...props} className={`w-full min-w-0 ${spacingClass} p-4 ${className}`}>
      {children}
    </DesignCard>
  );
}

export function ListHeader({
  children,
  title,
  subtitle,
  actions,
  as: Tag = 'div',
  className = '',
  ...props
}) {
  return (
    <DesignListToolbar
      as={Tag}
      flush
      {...props}
      className={`flex flex-wrap items-center justify-between gap-3 pb-4 ${className}`}
    >
      {title && <DesignCardHeading title={title} description={subtitle} className='mr-auto' />}
      {children}
      {actions && <div className='flex flex-wrap items-center gap-2'>{actions}</div>}
    </DesignListToolbar>
  );
}

export function ListFilters({ children, className = '', ...props }) {
  return (
    <DesignFilterPanel variant='card' data-list-filter {...props} className={className}>
      {children}
    </DesignFilterPanel>
  );
}

export function TableContainer({ children, className = '', ...props }) {
  return (
    <div
      role='region'
      aria-label='Tabel data'
      tabIndex={0}
      {...props}
      className={`w-full min-w-0 max-w-full overflow-x-auto overscroll-x-contain rounded-xl border border-gray-200 bg-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary ${className}`}
    >
      {children}
    </div>
  );
}

export function ListTable({
  children,
  className = '',
  compact = false,
  sticky = false,
  zebra = false,
  ...props
}) {
  return (
    <DesignDataTable
      {...props}
      compact={compact}
      sticky={sticky}
      zebra={zebra}
      className={className}
    >
      {children}
    </DesignDataTable>
  );
}

export function ListFooter({ children, className = '', ...props }) {
  return (
    <div
      {...props}
      className={`flex w-full flex-wrap items-center justify-between gap-3 pt-4 ${className}`}
    >
      {children}
    </div>
  );
}

export function ListPagination({
  dataLength,
  currentPage,
  perPage,
  pageCount,
  total,
  disabled = false,
  isLoading = false,
  pagination,
  handlePageClick,
  showPagination = dataLength > 0,
  className = '',
}) {
  return (
    <ListFooter className={className}>
      <TableSummary {...{ dataLength, currentPage, perPage, pageCount, total, isLoading }} />
      {pagination !== undefined
        ? pagination
        : showPagination && (
            <Paging
              {...{ currentPage, pageCount, handlePageClick }}
              disabled={disabled || isLoading}
            />
          )}
    </ListFooter>
  );
}

const actionTones = {
  view: 'text-blue-600 hover:bg-blue-50 hover:text-blue-700',
  primary: 'text-blue-600 hover:bg-blue-50 hover:text-blue-700',
  edit: 'text-amber-600 hover:bg-amber-50 hover:text-amber-700',
  delete: 'text-red-600 hover:bg-red-50 hover:text-red-700',
  refund: 'text-red-600 hover:bg-red-50 hover:text-red-700',
  cancel: 'text-red-600 hover:bg-red-50 hover:text-red-700',
  success: 'text-emerald-600 hover:bg-emerald-50 hover:text-emerald-700',
  default: 'text-gray-500 hover:bg-gray-100 hover:text-gray-700',
};

export function TableAction({
  as: Tag = 'button',
  tone = 'default',
  disabled = false,
  className = '',
  onClick,
  ...props
}) {
  return (
    <Tag
      {...(Tag === 'button' ? { type: 'button', disabled } : {})}
      {...props}
      aria-disabled={disabled || undefined}
      tabIndex={disabled ? -1 : props.tabIndex}
      onClick={(event) => {
        if (disabled) {
          event.preventDefault();
          event.stopPropagation();
        } else {
          onClick?.(event);
        }
      }}
      className={`inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-md border-0 bg-transparent p-0 shadow-none transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-500/20 ${disabled ? 'cursor-not-allowed opacity-50' : actionTones[tone] || actionTones.default} ${className}`}
    />
  );
}
